import { addDays, formatTime, isIsoDate, localDate, parseDate, zagrebToUtc } from "@mandrac/shared";

/**
 * What the date and time fields do with typed text. The form values stay ISO ("2026-11-04", "14:30");
 * the worker always sees and types Croatian order ("04.11.2026."), whatever language the browser is in.
 */

const SEPARATOR = /[.,/\- ]/;

/** Digits become dd.mm.yyyy while typing: dots go in between digits, never after the last one, so Backspace is not fought. */
export function maskDateText(raw: string): string {
  const max = [2, 2, 4] as const;
  const parts = [""];
  for (const ch of raw) {
    const i = parts.length - 1;
    if (/\d/.test(ch)) {
      if (parts[i]!.length < max[i as 0 | 1 | 2]) parts[i] += ch;
      else if (i < 2) parts.push(ch);
    } else if (SEPARATOR.test(ch) && parts[i] && i < 2) {
      parts.push("");
    }
  }
  return parts.join(".");
}

/** A whole date (four-digit year), or null while it is still being typed or does not exist. */
export function readDateText(text: string): string | null {
  const iso = parseDate(text.trim());
  if (!iso) return null;
  const year = Number(iso.slice(0, 4));
  return year >= 1900 && year <= 2099 ? iso : null;
}

/**
 * When the worker leaves the field: also takes "4.11." as this year. A short year is not taken: "05.11.20" is far
 * more likely a 2026 left unfinished than 2020, and a wrong year must not slip in unnoticed.
 */
export function finishDateText(text: string, thisYear: number): string | null {
  const m = /^(\d{1,2})\.(\d{1,2})\.?$/.exec(text.trim());
  if (!m) return readDateText(text);
  const iso = `${thisYear}-${m[2]!.padStart(2, "0")}-${m[1]!.padStart(2, "0")}`;
  return isIsoDate(iso) ? iso : null;
}

/** hh:mm while typing. */
export function maskTimeText(raw: string): string {
  const [h = "", ...rest] = raw.replace(/[^\d:.]/g, "").replace(/\./g, ":").split(":");
  if (rest.length) return `${h.slice(0, 2)}:${rest.join("").slice(0, 2)}`;
  return h.length > 2 ? `${h.slice(0, 2)}:${h.slice(2, 4)}` : h;
}

const validTime = (h: string, m: string) => (Number(h) <= 23 && Number(m) <= 59 ? `${h.padStart(2, "0")}:${m}` : null);

/** A whole time, or null while it is still being typed or does not exist. */
export function readTimeText(text: string): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  return m ? validTime(m[1]!, m[2]!) : null;
}

/** When the worker leaves the field: also takes a bare hour ("9", "14:"). "14:3" is an unfinished 14:35 as much as 14:30, so it is refused. */
export function finishTimeText(text: string): string | null {
  const m = /^(\d{1,2})(?::(\d{2})?)?$/.exec(text.trim());
  return m ? validTime(m[1]!, m[2] ?? "00") : null;
}

/** Weeks of a month, Monday first, with the days of the months around it; `month` is 1 to 12. */
export function monthWeeks(year: number, month: number): string[][] {
  const first = `${year}-${String(month).padStart(2, "0")}-01`;
  const weekday = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const start = addDays(first, -weekday);
  const weeks: string[][] = [];
  for (let w = 0; w < Math.ceil((weekday + days) / 7); w++) weeks.push(Array.from({ length: 7 }, (_, d) => addDays(start, w * 7 + d)));
  return weeks;
}

/**
 * Date and time in a form ("2026-07-15T14:30") are Zagreb time, like everything the app shows and calculates,
 * also on a device set to another time zone.
 */
export function instantToFormValue(iso: string | null | undefined): string {
  return iso ? `${localDate(iso)}T${formatTime(iso)}` : "";
}

export function formValueToInstant(value: string): string | null {
  if (!value) return null;
  const [date, time = "00:00"] = value.split("T") as [string, string?];
  const [hour = 0, minute = 0] = time.split(":").map(Number);
  return zagrebToUtc(date, hour, minute).toISOString();
}

/** For a button outside a form: asks the fields around it, and shows the first one that holds something that is not a date. */
export function datesReady(button: HTMLElement): boolean {
  const box = button.closest('[role="dialog"], form') ?? document.body;
  return [...box.querySelectorAll("input")].filter((i) => i.validity.customError).every((i) => i.reportValidity());
}
